import { useEffect, useRef, useState } from 'react'
import { ApiError } from './client'

export interface AsyncState<T> {
  data: T | null
  error: string | null
  /** No data to show yet. False during a background revalidation of cached data. */
  loading: boolean
  /** A request is in flight, whether or not there is already data on screen. */
  validating: boolean
}

/**
 * Responses already seen this session, keyed by `cacheKey`.
 *
 * Bounded and in memory only. The point is re-entry, not persistence: switching to
 * Metrics and back unmounts the view and throws its data away, and refetching a window
 * that has not changed to redraw the same numbers is the wait this removes.
 */
const CACHE = new Map<string, unknown>()

//: Small because entries are whole analysis results, and the working set is "the few
//: windows someone is moving between", not their entire session.
const MAX_ENTRIES = 32

function remember(key: string, value: unknown): void {
  // Re-insert so the key moves to the end: Map iterates in insertion order, which
  // makes the first key the least recently written.
  CACHE.delete(key)
  CACHE.set(key, value)
  while (CACHE.size > MAX_ENTRIES) {
    const oldest = CACHE.keys().next().value
    if (oldest === undefined) break
    CACHE.delete(oldest)
  }
}

/**
 * Drop every cached response.
 *
 * Called on sign-out and whenever the server says the session is gone. This deployment
 * is multi-user, so leaving one account's numbers in a Map that the next sign-in would
 * seed from is a disclosure, not just a stale read.
 */
export function clearAsyncCache(): void {
  CACHE.clear()
}

function seed<T>(cacheKey: string | undefined, enabled: boolean): AsyncState<T> {
  const cached = cacheKey === undefined ? undefined : (CACHE.get(cacheKey) as T | undefined)
  if (cached !== undefined) return { data: cached, error: null, loading: false, validating: enabled }
  return { data: null, error: null, loading: enabled, validating: enabled }
}

/**
 * Run an async fetch tied to the component lifecycle.
 *
 * Requests are aborted when `deps` change or the component unmounts, so a slow
 * response for an abandoned date range can never overwrite a newer one. `enabled`
 * defers the fetch for inputs the user has not supplied yet.
 *
 * Passing `cacheKey` opts into stale-while-revalidate: a key seen before renders its
 * previous response on the first frame and refetches behind it. The request still goes
 * out every time, so nothing that relied on remounting to see fresh data stops doing
 * so — only the placeholder in front of it disappears. Omitting the key keeps the
 * plain fetch-on-mount behaviour.
 *
 * The key must identify everything the response depends on — the property above all.
 * A key that collides across properties would paint one property's numbers under
 * another's name, which is the flash the shell's `key={property_id}` remount prevents.
 */
export function useAsync<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  enabled = true,
  cacheKey?: string,
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>(() => seed<T>(cacheKey, enabled))

  // Swapping to a different key mid-mount must not leave the previous key's data on
  // screen for a frame. Resetting during render rather than in an effect means React
  // re-renders before committing, so the wrong numbers are never painted.
  const [activeKey, setActiveKey] = useState(cacheKey)
  if (cacheKey !== activeKey) {
    setActiveKey(cacheKey)
    setState(seed<T>(cacheKey, enabled))
  }

  // Read in the effect rather than captured in its dependency list: the key is derived
  // from `deps`, and listing it too would re-run the fetch twice for one change.
  const keyRef = useRef(cacheKey)
  keyRef.current = cacheKey

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, error: null, loading: false, validating: false })
      return
    }

    const controller = new AbortController()
    // Anything already cached stays on screen while this runs; only a cold key shows
    // the placeholder.
    setState((previous) => ({
      ...previous,
      loading: previous.data === null,
      validating: true,
      error: null,
    }))

    fetcher(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return
        if (keyRef.current !== undefined) remember(keyRef.current, data)
        setState({ data, error: null, loading: false, validating: false })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        const message =
          error instanceof ApiError
            ? error.message
            : error instanceof Error
              ? error.message
              : 'Something went wrong.'
        // The stale reading is dropped alongside the error rather than left under it:
        // a banner above numbers from a window the reader has since moved off would
        // read as though those numbers were the failure.
        setState({ data: null, error: message, loading: false, validating: false })
      })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled])

  return state
}
